import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { AuthToken, User } from '../types';
import { safeStorage } from '../utils/safeStorage';

// API response type definitions
export interface InterestedVehicleResponse {
  id: string;
  customer_id: string;
  vehicle_id: string;
  priority_order: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface StockRecommendation {
  id: string;
  vehicle_make: string;
  vehicle_model: string;
  priority_level: string;
  recommendation_score: number;
  [key: string]: unknown;
}

export interface TurnoverAnalysis {
  analysis_period: string;
  average_days_to_sell: number;
  turnover_rate: number;
  [key: string]: unknown;
}

export interface ProfitOptimization {
  optimization_type: string;
  potential_profit: number;
  recommendation: string;
  [key: string]: unknown;
}

export interface DemandForecast {
  forecast_period: string;
  predicted_demand: number;
  confidence_level: number;
  [key: string]: unknown;
}

export interface StockingGuideSummary {
  total_vehicles: number;
  avg_days_in_stock: number;
  turnover_rate: number;
  [key: string]: unknown;
}

class ApiService {
  private client: AxiosInstance;
  private baseURL: string;
  private accessToken: string | null = null;
  private refreshTokenValue: string | null = null;

  constructor() {
    // Production: REACT_APP_API_URL="" → same-origin (Caddy proxies /api/* to backend)
    // Development: REACT_APP_API_URL unset → fallback to localhost:8000
    // Explicit: REACT_APP_API_URL="https://dev.carlotpilot.com" → use that URL
    const envUrl = process.env.REACT_APP_API_URL;
    this.baseURL = envUrl === '' ? '' : envUrl || 'http://localhost:8000';

    this.client = axios.create({
      baseURL: `${this.baseURL}/api/v1`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Get the backend base URL (for raw fetch() calls that bypass axios).
   * Production: "" (same-origin), Dev: "http://localhost:8000"
   */
  getBaseUrl(): string {
    return this.baseURL;
  }

  /**
   * Build a full URL for backend-served static files (uploads, images).
   * In production (same-origin via Caddy): returns "/uploads/path"
   * In development: returns "http://localhost:8000/uploads/path"
   */
  getFileUrl(path: string): string {
    if (!path) return '';
    // If path already starts with http(s), return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${this.baseURL}${path}`;
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getStoredToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const url = originalRequest?.url || '';

        // Never intercept auth endpoints – let the caller handle their errors
        if (
          url.includes('/auth/login') ||
          url.includes('/auth/refresh') ||
          url.includes('/auth/token')
        ) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // If no refresh token exists (public visitor), skip refresh attempt
          const storedRefresh = this.getStoredRefreshToken();
          if (!storedRefresh) {
            return Promise.reject(error);
          }

          try {
            await this.refreshToken();
            const newToken = this.getStoredToken();
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed – clear tokens and let React handle the redirect
            // (ProtectedRoute will navigate to /login when isAuthenticated becomes false)
            this.clearTokens();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private getStoredToken(): string | null {
    return this.accessToken ?? safeStorage.getItem('access_token');
  }

  private getStoredRefreshToken(): string | null {
    return this.refreshTokenValue ?? safeStorage.getItem('refresh_token');
  }

  private setTokens(tokens: AuthToken): void {
    this.accessToken = tokens.access_token;
    this.refreshTokenValue = tokens.refresh_token;
    this.client.defaults.headers.common.Authorization = `Bearer ${tokens.access_token}`;
    safeStorage.setItem('access_token', tokens.access_token);
    safeStorage.setItem('refresh_token', tokens.refresh_token);
  }

  private clearTokens(): void {
    this.accessToken = null;
    this.refreshTokenValue = null;
    delete this.client.defaults.headers.common.Authorization;
    safeStorage.removeItem('access_token');
    safeStorage.removeItem('refresh_token');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('auth:token-cleared'));
    }
  }

  // Authentication methods
  async login(email: string, password: string): Promise<AuthToken> {
    const response = await this.client.post<AuthToken>('/auth/login', {
      email,
      password,
    });

    this.setTokens(response.data);
    return response.data;
  }

  async refreshToken(): Promise<AuthToken> {
    const refreshToken = this.getStoredRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await this.client.post<AuthToken>('/auth/refresh', {
      refresh_token: refreshToken,
    });

    this.setTokens(response.data);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout');
    } catch (error) {
      // Ignore logout errors
    } finally {
      this.clearTokens();
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get('/auth/me');
    return response.data;
  }

  // Customer Interested Vehicles methods
  async addInterestedVehicle(
    customerId: string,
    vehicleId: string,
    priority: number = 0,
    notes?: string
  ): Promise<InterestedVehicleResponse> {
    const response = await this.client.post(`/customers/${customerId}/interested-vehicles`, {
      vehicle_id: vehicleId,
      priority_order: priority,
      notes: notes || '',
    });
    return response.data;
  }

  async removeInterestedVehicle(customerId: string, vehicleId: string): Promise<void> {
    const response = await this.client.delete(
      `/customers/${customerId}/interested-vehicles/${vehicleId}`
    );
    return response.data;
  }

  async updateInterestedVehicle(
    customerId: string,
    vehicleId: string,
    data: { priority_order?: number; notes?: string }
  ): Promise<InterestedVehicleResponse> {
    const response = await this.client.put(
      `/customers/${customerId}/interested-vehicles/${vehicleId}`,
      data
    );
    return response.data;
  }

  // Stocking Guide methods
  async getStockRecommendations(params?: {
    skip?: number;
    limit?: number;
    vehicle_make?: string;
    vehicle_model?: string;
    priority_level?: string;
  }): Promise<StockRecommendation[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
      if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params.vehicle_make) queryParams.append('vehicle_make', params.vehicle_make);
      if (params.vehicle_model) queryParams.append('vehicle_model', params.vehicle_model);
      if (params.priority_level) queryParams.append('priority_level', params.priority_level);
    }
    const query = queryParams.toString();
    const response = await this.client.get(
      `/stocking-guide/recommendations/${query ? '?' + query : ''}`
    );
    return response.data;
  }

  async getTurnoverAnalysis(params?: {
    skip?: number;
    limit?: number;
    analysis_period?: string;
  }): Promise<TurnoverAnalysis[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
      if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params.analysis_period) queryParams.append('analysis_period', params.analysis_period);
    }
    const query = queryParams.toString();
    const response = await this.client.get(
      `/stocking-guide/turnover-analysis/${query ? '?' + query : ''}`
    );
    return response.data;
  }

  async getProfitOptimizations(params?: {
    skip?: number;
    limit?: number;
    optimization_type?: string;
  }): Promise<ProfitOptimization[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
      if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params.optimization_type)
        queryParams.append('optimization_type', params.optimization_type);
    }
    const query = queryParams.toString();
    const response = await this.client.get(
      `/stocking-guide/profit-optimization/${query ? '?' + query : ''}`
    );
    return response.data;
  }

  async getDemandForecasts(params?: {
    skip?: number;
    limit?: number;
    forecast_period?: string;
  }): Promise<DemandForecast[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
      if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params.forecast_period) queryParams.append('forecast_period', params.forecast_period);
    }
    const query = queryParams.toString();
    const response = await this.client.get(
      `/stocking-guide/demand-forecasting/${query ? '?' + query : ''}`
    );
    return response.data;
  }

  async getStockingGuideSummary(): Promise<StockingGuideSummary> {
    const response = await this.client.get('/stocking-guide/dashboard/summary');
    return response.data;
  }

  async generateStockRecommendations(
    criteria: Record<string, unknown>
  ): Promise<StockRecommendation[]> {
    const response = await this.client.post('/stocking-guide/generate-recommendations', criteria);
    return response.data;
  }

  async analyzeTurnover(analysisParams: { analysis_period: string }): Promise<TurnoverAnalysis> {
    const response = await this.client.post('/stocking-guide/analyze-turnover', analysisParams);
    return response.data;
  }

  // Generic CRUD methods
  async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(endpoint, config);
    return response.data;
  }

  async post<T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(endpoint, data, config);
    return response.data;
  }

  async put<T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(endpoint, data, config);
    return response.data;
  }

  async patch<T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(endpoint, data, config);
    return response.data;
  }

  async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(endpoint, config);
    return response.data;
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.getStoredToken();
  }

  getAuthHeader(): Record<string, string> {
    const token = this.getStoredToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
}

// Create singleton instance
export const apiService = new ApiService();
export default apiService;

// Extract a user-facing error message from an axios/apiService failure.
// Handles both shapes the backend uses:
//   { "error": { "message": "..." } }   ← custom HTTPException middleware
//   { "detail": "..." }                  ← FastAPI default
// Falls back to the provided string when no structured message is present.
export function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (typeof error !== 'object' || error === null) return fallback;
  const e = error as {
    response?: { data?: { error?: { message?: string }; detail?: string | { msg?: string }[] } };
  };
  const data = e?.response?.data;
  if (!data) return fallback;
  if (data.error?.message) return data.error.message;
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
  return fallback;
}
